import { Button } from '@design-system';
import { WaterlilyLogo } from '@/components/WaterlilyLogo';
import { IntentSelector } from './intent-selector';
import { ConsultForm } from './consult-form';
import { LicensedDone } from './licensed-done';

import { X } from 'lucide-react';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { FREEMIUM_ONBOARDING_CALL_URL, getFreemiumIntentFlow } from '@shared';
import { analytics } from '@/analytics';

import { useFreemiumIntentCapture } from '../hooks/use-freemium-intent-capture';
import { freemiumIntentToolPath } from '../lib/intent-routing';

import type { FreemiumIntentId } from '@shared';

interface FreemiumIntentRouterProps {
  clientId: string;
  clientFirstName: string;
  onClose: () => void;
}

type RouterStep = 'intent' | 'consult' | 'done';

function trackIntent(clientId: string, intent: FreemiumIntentId) {
  try {
    analytics.track('freemium_intent_selected', { clientId, intent, context: 'router' }, 'action');
  } catch {
    // Metrics must never break the flow.
  }
}

/**
 * "What are you trying to do next?" router for unlocked freemium advisors.
 * Picking an intent routes straight to that action (no paywall); the open-ended
 * intents collect a note for the team and then drop the advisor back into their
 * plan.
 */
export function FreemiumIntentRouter({
  clientId,
  clientFirstName,
  onClose,
}: FreemiumIntentRouterProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<RouterStep>('intent');
  const [intent, setIntent] = useState<FreemiumIntentId | null>(null);
  const capture = useFreemiumIntentCapture(clientId);

  function handleContinue() {
    if (!intent) return;
    trackIntent(clientId, intent);

    const toolPath = freemiumIntentToolPath(clientId, intent);
    if (toolPath) {
      navigate(toolPath);
      return onClose();
    }

    if (intent === 'self-funding') {
      navigate(`/clients/${clientId}/onboarding/guided-planning`);
      return onClose();
    }

    // comprehensive-plan / something-else: collect a note for the team.
    setStep('consult');
  }

  function handleConsultSubmit(details: string) {
    if (!intent) return;
    capture.mutate({ intent, details }, { onSuccess: () => setStep('done') });
  }

  const flow = intent ? getFreemiumIntentFlow(intent) : null;

  return (
    <div className="bg-background fixed inset-0 z-50 flex flex-col">
      <header className="flex shrink-0 items-center justify-between border-b p-4 md:px-6">
        <WaterlilyLogo className="h-7 w-auto" />
        <Button
          type="button"
          aria-label="Close"
          onClick={onClose}
          variant="outline"
          size="icon-sm"
          className="rounded-full"
        >
          <X />
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="flex min-h-[calc(100vh-70px)] items-center justify-center px-4 py-10 md:px-6">
          {step === 'intent' && (
            <IntentSelector
              clientFirstName={clientFirstName}
              value={intent}
              onChange={setIntent}
              onContinue={handleContinue}
              onBack={onClose}
              footer={
                <p className="text-muted-foreground mt-6 text-center text-sm">
                  Our team is here to help.{' '}
                  <a
                    href={FREEMIUM_ONBOARDING_CALL_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary underline!"
                  >
                    Book a call
                  </a>
                </p>
              }
            />
          )}

          {step === 'consult' && (
            <ConsultForm
              prompt={flow?.freeTextPrompt ?? 'Tell us a bit more about what you have in mind.'}
              onSubmit={handleConsultSubmit}
              onBack={() => setStep('intent')}
              isPending={capture.isPending}
            />
          )}

          {step === 'done' && (
            <LicensedDone
              clientFirstName={clientFirstName}
              variant="consult"
              onClose={onClose}
              closeLabel="Continue to your plan"
            />
          )}
        </div>
      </div>
    </div>
  );
}
